import { type RootState } from "../../redux/store";
import { NumberSystem } from "../types/types";
import { createSelector } from "reselect";
import { HOUSING_SA_DIMENIONS_CODES } from "../constants";
import { convertProductsToTabularViewData } from "../../Search/redux/searchSelectors";
import { type HSATabularColumnConfig } from "~/HousingSelectionAssistant/redux/types";
import { toMapFn } from "~/utils/reducers";
import { type RuntimeFilterDefinition } from "~/HousingSelectionAssistant/types/FilterDefinitionV2";

export const selectTabularViewConfig = (state: RootState, numberSystem: NumberSystem) =>
  state.housingSA.configuration[numberSystem === NumberSystem.Imperial ? "imperialColumnConfig" : "metricColumnConfig"];
export const selectIsInitialised = (state: RootState) => state.housingSA.initialised;
export const selectIsInFlight = (state: RootState) => state.housingSA.inFlight;
export const selectLastSearchReason = (state: RootState) => state.housingSA.lastSearchReason;
export const selectIsError = (state: RootState) => state.housingSA.error;
export const selectTotalNumberOfResults = (state: RootState) => state.housingSA.searchResults.totalResults;

export const selectFacets = (state: RootState) => state.housingSA.searchResults.facetsByCode;

export const selectDimensionTileConfig = createSelector(
  (state: RootState) => state.housingSA.configuration.metricColumnConfig,
  (filterConfig) => {
    return filterConfig.filter(isValidColumnDef(HOUSING_SA_DIMENIONS_CODES as unknown as string[])).map((item) => ({
      label: "" + item.assistantLabel,
      code: item.techValueUID,
    }));
  }
);

export const selectFilterConfig = createSelector(
  (state: RootState) => state.housingSA.configuration.filterConfig,
  (state: RootState) => state.housingSA.searchResults.facetsByCode,
  (filterConfig, facets) => {
    const filters: RuntimeFilterDefinition[] = Object.values(filterConfig).map((item) => ({
      ...item,
      items: item.items.map((item) => {
        const facet = facets[item.facetCode ?? ""];
        const facetValue = facet?.values.find((fv) => fv.code === item.facetValueCode);
        const count = facetValue?.count ?? 0;
        return {
          ...item,
          count,
          disabled: count === 0,
        };
      }),
    }));
    return toMapFn(filters, (item) => item.code);
  }
);

export const selectSingleFacetConfig = (code: string) => (state: RootState) => selectFilterConfig(state)[code];

const isValidColumnDef = (values: string[]) => (val: HSATabularColumnConfig) => values.includes(val.techValueUID);

export const selectHSATabularViewData = createSelector(
  (state: RootState) => state.housingSA.searchResults.results,
  (state: RootState) => state.products.basic,
  (results, productMap) => {
    return convertProductsToTabularViewData(results, productMap);
  }
);
